/**
 * Premium "Letterbox Intro" — cinema bars in.
 *
 * Visual design:
 *  - Top + bottom black bars slide IN to lock the scene into widescreen.
 *  - As bars settle, an accent hairline draws across the inner edges.
 *  - Title rises from below center, eyebrow reveals above, brand stamp
 *    appears in lower-right corner.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors, brandName,
} from "../../reel/sceneShared.js";

export const letterboxIntro: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver Cinema";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  const barH = isVertical ? "16%" : "14%";

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const topBarId = `${clipPrefix}-bar-top`;
  const botBarId = `${clipPrefix}-bar-bot`;
  const topLineId = `${clipPrefix}-line-top`;
  const botLineId = `${clipPrefix}-line-bot`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;
  const stampId = `${clipPrefix}-stamp`;
  const tcId = `${clipPrefix}-tc`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const stampMarkup = brandLogoImg({ id: `${stampId}-img`, sizePx: isVertical ? 48 : 56 });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${topBarId}" style="position:absolute;left:0;right:0;top:0;height:${barH};background:#000;z-index:5;"></div>
  <div id="${botBarId}" style="position:absolute;left:0;right:0;bottom:0;height:${barH};background:#000;z-index:5;"></div>
  <div id="${topLineId}" style="position:absolute;left:0;right:0;top:${barH};height:2px;background:${accent};box-shadow:0 0 16px ${withAlpha(accent, 0.7)};transform-origin:50% 50%;transform:scaleX(0);z-index:6;"></div>
  <div id="${botLineId}" style="position:absolute;left:0;right:0;bottom:${barH};height:2px;background:${accent};box-shadow:0 0 16px ${withAlpha(accent, 0.7)};transform-origin:50% 50%;transform:scaleX(0);z-index:6;"></div>

  <div id="${tcId}" style="position:absolute;left:64px;top:calc(${barH} + 28px);font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize * 0.75}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.65)};opacity:0;z-index:7;">2.40 : 1 · 24fps</div>

  <div id="${stampId}" style="position:absolute;right:64px;bottom:calc(${barH} + 28px);display:flex;align-items:center;gap:14px;opacity:0;z-index:7;">
    ${stampMarkup}
    <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize * 0.7}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.85)};">${escapeHtml(brandName)}</div>
  </div>

  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 32 : 24}px;padding:96px;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— A Featured Reel —</div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${topBarId}", { y: "-100%" });`,
    `gsap.set("#${botBarId}", { y: "100%" });`,
    `gsap.set("#${topLineId}", { scaleX: 0 });`,
    `gsap.set("#${botLineId}", { scaleX: 0 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 36 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
    `gsap.set("#${eyeId}", { opacity: 0, y: -18 });`,
    `gsap.set("#${stampId}", { opacity: 0, y: 12 });`,
    `gsap.set("#${tcId}", { opacity: 0 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Bars slide in.
    t.to(`#${topBarId}`, { y: 0, duration: 0.85, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${botBarId}`, { y: 0, duration: 0.85, ease: "raw:\"power3.out\"" }, 0.05),
    // Hairlines draw.
    t.to(`#${topLineId}`, { scaleX: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.5),
    t.to(`#${botLineId}`, { scaleX: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.5),
    // Eyebrow + headline + sub.
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.7),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.85),
    t.to(`#${tcId}`, { opacity: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.95),
    t.to(`#${stampId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.05),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.2));

  // Subtle headline drift.
  const dwell = Math.max(0.6, clipDurationSec - 1.6);
  tweens.push(t.to(`#${headId}`, { y: -10, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.4));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
